import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header';
import DoctorBottomNav from '../../components/doctor/DoctorBottomNav';
import { InlineLoader } from '../../components/common/Loader';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { getDoctorConversations, deleteConversation } from '../../services/doctorService';
import DeleteChatModal from '../../components/common/DeleteChatModal';
import toast from 'react-hot-toast';
import { truncate } from '../../utils/helpers';

// ── Time display ───────────────────────────────────────────────────────────
function msgTime(dateStr) {
  if (!dateStr) return '';
  const d    = new Date(dateStr);
  const now  = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60)       return 'just now';
  if (diff < 3600)     return `${Math.floor(diff/60)}m`;
  if (diff < 86400)    return d.toLocaleTimeString('en',{hour:'2-digit',minute:'2-digit',hour12:true});
  if (diff < 604800)   return d.toLocaleDateString('en',{weekday:'short'});
  return d.toLocaleDateString('en',{day:'numeric',month:'short'});
}

// ── Patient Chat Card ──────────────────────────────────────────────────────
function PatientChatCard({ conv, myId, onLongPress }) {
  const navigate = useNavigate();
  const last     = conv.lastMessage;

  // Extract patient id from conversation

  const preview = !last
    ? 'No messages yet'
    : last.type === 'image'  ? '📷 Photo'
    : last.type === 'pdf'    ? '📄 Document'
    : last.type === 'voice'  ? '🎙 Voice note'
    : last.type === 'system' ? '🔔 ' + truncate(last.message||'',35)
    : truncate(last.message || '', 45);

  const isUnread  = conv.unreadCount > 0;
  const isMine    = last?.senderId === myId;

  const pressTimer = useRef(null);
  const handlePressStart = () => { pressTimer.current = setTimeout(() => onLongPress(conv), 600); };
  const handlePressEnd   = () => { clearTimeout(pressTimer.current); };

  return (
    <div className="relative group">
    <button
      onClick={() => navigate(`/doctor/chat/${conv._id}`, {
        state: { patientName: conv.patientName || 'Patient' }
      })}
      onMouseDown={handlePressStart} onMouseUp={handlePressEnd} onMouseLeave={handlePressEnd}
      onTouchStart={handlePressStart} onTouchEnd={handlePressEnd}
      className="w-full flex items-center gap-3 px-4 py-3.5 bg-white border-b border-border active:bg-surface text-left transition-colors"
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-primary-light flex items-center justify-center">
          <span className="text-primary font-bold text-base">
            {(conv.patientName || 'P')[0].toUpperCase()}
          </span>
        </div>
        {isUnread && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-primary text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <p className="text-sm font-bold text-text-primary truncate">
            {conv.patientName || 'Patient'}
          </p>
          <span className="text-[10px] text-text-muted flex-shrink-0 ml-2">
            {msgTime(last?.createdAt)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {isMine && <i className={`fa-solid fa-check-double text-[9px] ${last?.isRead?'text-blue-400':'text-text-muted'}`} />}
          <p className={`text-xs truncate ${isUnread ? 'text-text-primary font-semibold' : 'text-text-muted'}`}>
            {isMine ? 'You: ' : ''}{preview}
          </p>
        </div>
        {/* Slot time badge */}
        {conv.slotTime && (
          <span className="inline-flex items-center gap-1 text-[9px] text-primary bg-primary-light px-1.5 py-0.5 rounded-full mt-0.5">
            <i className="fa-solid fa-clock text-[8px]" />{conv.slotTime}
          </span>
        )}
      </div>

      <i className="fa-solid fa-chevron-right text-slate-200 text-xs flex-shrink-0" />
    </button>
    {/* Desktop hover delete */}
    <button
      onClick={(e) => { e.stopPropagation(); onLongPress(conv); }}
      className="absolute right-10 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center justify-center w-8 h-8 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
      title="Delete chat">
      <i className="fa-solid fa-trash-can text-danger text-xs" />
    </button>
    </div>
  );
}

// ── Medicine Store Chat Card ───────────────────────────────────────────────
function StoreChatCard({ conv, myId, onLongPress }) {
  const pressTimer2 = useRef(null);
  const navigate    = useNavigate();
  const last     = conv.lastMessage;

  const preview = !last
    ? 'No messages yet'
    : last.type === 'image' ? '📷 Photo'
    : last.type === 'pdf'   ? '📄 Prescription/Document'
    : truncate(last.message||'',45);

  const isUnread  = conv.unreadCount > 0;
  // Show patient phone as display name (since this is medicine store)
  const displayName = conv.patientPhone
    ? `+91 ${conv.patientPhone}`
    : conv.patientName || 'Patient Enquiry';

  const initial = conv.patientPhone
    ? conv.patientPhone.slice(-2)
    : (conv.patientName||'P')[0].toUpperCase();

  return (
    <div className="relative group">
    <button
      onClick={() => navigate(`/doctor/medicine-chat/${conv._id}`, {
        state: { patientName: displayName }
      })}
      onMouseDown={() => { pressTimer2.current = setTimeout(() => onLongPress(conv), 600); }}
      onMouseUp={() => clearTimeout(pressTimer2.current)}
      onMouseLeave={() => clearTimeout(pressTimer2.current)}
      onTouchStart={() => { pressTimer2.current = setTimeout(() => onLongPress(conv), 600); }}
      onTouchEnd={() => clearTimeout(pressTimer2.current)}
      className="w-full flex items-center gap-3 px-4 py-3.5 bg-white border-b border-border active:bg-surface text-left transition-colors"
    >
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <span className="text-green-700 font-bold text-sm">{initial}</span>
        </div>
        {isUnread && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-green-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          {/* Show patient phone number as title */}
          <p className="text-sm font-bold text-text-primary truncate">{displayName}</p>
          <span className="text-[10px] text-text-muted flex-shrink-0 ml-2">
            {msgTime(last?.createdAt)}
          </span>
        </div>
        <p className={`text-xs truncate ${isUnread ? 'text-text-primary font-semibold' : 'text-text-muted'}`}>
          {preview}
        </p>
        <span className="inline-flex items-center gap-1 text-[9px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full mt-0.5">
          <i className="fa-solid fa-pills text-[8px]" />Medicine Enquiry
        </span>
      </div>
      <i className="fa-solid fa-chevron-right text-slate-200 text-xs flex-shrink-0" />
    </button>
    {/* Desktop hover delete */}
    <button
      onClick={(e) => { e.stopPropagation(); onLongPress(conv); }}
      className="absolute right-10 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center justify-center w-8 h-8 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
      title="Delete chat">
      <i className="fa-solid fa-trash-can text-danger text-xs" />
    </button>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function DoctorChatList() {
  const navigate    = useNavigate();
  const { role, user } = useAuth();
  const { socket }  = useSocket();
  // FIX: Staff ke liye doctorId use karo — patient ne conversation doctorId se banayi hai
  const myId = (role === 'staff' && user?.doctorId)
    ? user.doctorId.toString()
    : user?._id?.toString();

  const [activeTab, setActiveTab]       = useState('patients');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [patientChats, setPatientChats] = useState([]);
  const [storeChats, setStoreChats]     = useState([]);
  const [loading, setLoading]           = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDoctorConversations();
      setPatientChats(res.conversations    || []);
      setStoreChats(res.storeConversations || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real-time: refresh on new message
  useEffect(() => {
    if (!socket) return;
    const onMsg = () => load();
    socket.on('receive-message', onMsg);
    return () => socket.off('receive-message', onMsg);
  }, [socket, load]);

  const patientUnread = patientChats.reduce((s,c) => s + (c.unreadCount||0), 0);
  const storeUnread   = storeChats.reduce((s,c)   => s + (c.unreadCount||0), 0);
  const totalUnread   = patientUnread + storeUnread;

  const handleDelete = async (deleteFor) => {
    if (!deleteTarget) return;
    const targetId = deleteTarget._id;
    // Optimistically remove immediately
    setPatientChats(prev => prev.filter(c => c._id !== targetId));
    setStoreChats(prev => prev.filter(c => c._id !== targetId));
    setDeleteTarget(null);
    try {
      await deleteConversation(targetId, deleteFor);
      toast.success(deleteFor === 'both' ? 'Deleted for everyone' : 'Chat removed');
      load(); // Sync with server
    } catch (err) {
      toast.error(err.message || 'Delete failed');
      load(); // Restore on error
    }
  };

  // Staff permission guard
  if (role === 'staff' && !user?.permissions?.['chat']) {
    return (
      <div className="page-container flex flex-col items-center justify-center py-20 gap-4 text-center px-8">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
          <i className="fa-solid fa-lock text-red-500 text-3xl" />
        </div>
        <h2 className="text-lg font-bold text-text-primary">Access Restricted</h2>
        <p className="text-sm text-text-muted leading-relaxed">
          You don&apos;t have permission to access this section.
        </p>
        <button onClick={() => window.history.back()} className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-bold">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="max-w-2xl mx-auto">
      <Header title="Chats" showBack showNotif={false} />

      {/* Tabs */}
      <div className="sticky top-14 z-20 bg-white border-b border-border flex">
        <button
          onClick={() => setActiveTab('patients')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'patients' ? 'border-primary text-primary' : 'border-transparent text-text-muted'
          }`}
        >
          <i className="fa-solid fa-user-injured text-sm" />
          Patient Chats
          {patientUnread > 0 && (
            <span className="w-5 h-5 bg-primary text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {patientUnread > 9 ? '9+' : patientUnread}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('medicine')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold border-b-2 transition-all ${
            activeTab === 'medicine' ? 'border-green-600 text-green-700' : 'border-transparent text-text-muted'
          }`}
        >
          <i className="fa-solid fa-pills text-sm" />
          Medicine Store
          {storeUnread > 0 && (
            <span className="w-5 h-5 bg-green-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {storeUnread > 9 ? '9+' : storeUnread}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <InlineLoader text="Loading chats..." />
      ) : activeTab === 'patients' ? (
        patientChats.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-4 text-center px-8">
            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center">
              <i className="fa-solid fa-comment-slash text-text-muted text-2xl" />
            </div>
            <div>
              <p className="font-semibold text-text-primary">No patient chats yet</p>
              <p className="text-sm text-text-muted mt-1 leading-relaxed">
                Patient chats appear after video appointment slots are confirmed and active.
              </p>
            </div>
          </div>
        ) : (
          <div className="pb-24">
            <div className="px-4 py-2 bg-slate-50 border-b border-border">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Video Consultation Chats{patientUnread > 0 && ` · ${patientUnread} unread`}
              </p>
            </div>
            {patientChats.map(c => <PatientChatCard key={c._id} conv={c} myId={myId} onLongPress={setDeleteTarget} />)}
          </div>
        )
      ) : (
        storeChats.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-4 text-center px-8">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
              <i className="fa-solid fa-store text-green-400 text-2xl" />
            </div>
            <div>
              <p className="font-semibold text-text-primary">No medicine enquiries yet</p>
              <p className="text-sm text-text-muted mt-1">
                Patients can message your medicine store from your profile page.
              </p>
            </div>
            <button onClick={() => navigate('/doctor/profile')}
              className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold">
              Set Up Medicine Store
            </button>
          </div>
        ) : (
          <div className="pb-24">
            <div className="px-4 py-2 bg-slate-50 border-b border-border">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Medicine Store Enquiries{storeUnread > 0 && ` · ${storeUnread} unread`}
              </p>
            </div>
            {storeChats.map(c => <StoreChatCard key={c._id} conv={c} myId={myId} onLongPress={setDeleteTarget} />)}
          </div>
        )
      )}

      </div>

      {deleteTarget && (
        <DeleteChatModal
          onDeleteForMe={() => handleDelete('me')}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      <DoctorBottomNav notifCount={totalUnread} />
    </div>
  );
}
